import { useMemo, useState } from "react";
import type React from "react";
import {
  ActionIcon,
  Group,
  Loader,
  Select,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { Button, Table } from "@xplatform/design-system";
import { IconPlus, IconSend, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useItemMpSearch } from "@hooks/jobResults/useItemMpSearch";
import type { ItemMpMatchViewModel } from "@models/job";
import type { TmVariant } from "@models/mappingResult";
import { PLACEHOLDER } from "@utils/rowHelpers";
import formatVariantLabel, { variantIdentity } from "@utils/variantLabel";

const optionLabel = (item: ItemMpMatchViewModel): string =>
  `${item.itemMpId} - ${item.itemMpText}`;

interface TmAddRowProps {
  colSpan: number;
  jobId: string | undefined;
  variants: TmVariant[];
  onAddRow: (
    itemMpId: number,
    itemMpText: string,
    value: string,
    variant: TmVariant | null,
  ) => Promise<void>;
}

const TmAddRow: React.FC<TmAddRowProps> = ({
  colSpan,
  jobId,
  variants,
  onAddRow,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Hold the picked item itself, not just its id: every new search replaces
  // `itemMp.items`, so an id looked up there goes stale the moment the reviewer
  // types again without re-picking.
  const [picked, setPicked] = useState<ItemMpMatchViewModel | null>(null);
  const [value, setValue] = useState("");
  const [variant, setVariant] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const itemMp = useItemMpSearch(jobId);

  const variantOptions = useMemo(
    () =>
      variants.map((item) => ({
        value: variantIdentity(item),
        label: formatVariantLabel(item),
      })),
    [variants],
  );

  // Keep the picked option in `data` even once it drops out of the result page,
  // otherwise Mantine has no label to render for the selected value.
  const options = useMemo(() => {
    const items = [...itemMp.items];
    if (picked && !items.some((it) => it.itemMpId === picked.itemMpId)) {
      items.unshift(picked);
    }
    return items.map((it) => ({
      value: String(it.itemMpId),
      label: optionLabel(it),
    }));
  }, [itemMp.items, picked]);

  const handlePick = (next: string | null): void => {
    if (next === null) {
      setPicked(null);
      return;
    }
    const found = itemMp.items.find((it) => String(it.itemMpId) === next);
    if (found) setPicked(found);
  };

  // Picking an option makes Mantine echo its label back through onSearchChange.
  // Searching for that label would fetch a page nothing matches and empty the
  // dropdown, so only a reviewer's own typing reaches the server.
  const handleSearchChange = (search: string): void => {
    if (picked && search === optionLabel(picked)) return;
    itemMp.search(search);
  };

  const reset = (): void => {
    setPicked(null);
    setValue("");
    setVariant(null);
  };

  const close = (): void => {
    reset();
    setOpen(false);
  };

  const canSubmit =
    picked !== null &&
    value.trim().length > 0 &&
    (variantOptions.length === 0 || variant !== null) &&
    !submitting;

  const handleSave = async (): Promise<void> => {
    if (
      !picked ||
      value.trim().length === 0 ||
      (variantOptions.length > 0 && variant === null)
    ) {
      return;
    }
    setSubmitting(true);
    try {
      await onAddRow(
        picked.itemMpId, picked.itemMpText, value.trim(),
        variants.find((item) => variantIdentity(item) === variant) ?? null,
      );
      close();
    } catch {
      // onAddRow surfaces its own error toast; keep the typed value so the
      // reviewer can retry without re-entering it.
    } finally {
      setSubmitting(false);
    }
  };

  // Collapsed: a single full-width affordance to open the inline editor.
  if (!open) {
    return (
      <Table.Tr className="job-results-page__add-row">
        <Table.Td colSpan={colSpan}>
          <Group justify="center">
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => setOpen(true)}
            >
              {t("jobResultsPage.addRow.button", { defaultValue: "Add row" })}
            </Button>
          </Group>
        </Table.Td>
      </Table.Tr>
    );
  }

  // Open: lay the editor out cell-by-cell so each input sits under the column
  // it maps to — the raw value under "Value", the catalogue search under
  // "Item-MP Listing", the picked id under "ItemMP ID" — so the reviewer sees
  // that the text they type is the raw value, matched to the Item-MP they pick.
  return (
    <Table.Tr className="job-results-page__add-row job-results-page__add-row--editing">
      {/* # */}
      <Table.Td className="job-results-page__tm-index-cell">
        <IconPlus
          size={14}
          color="var(--mantine-color-dimmed)"
          aria-hidden
        />
      </Table.Td>

      {/* Source path — choose the vehicle variant for the manual row */}
      <Table.Td>
        <Select
          label={t("jobResultsPage.addRow.variant", {
            defaultValue: "Variant",
          })}
          placeholder={t("jobResultsPage.addRow.variantPlaceholder", {
            defaultValue: "Select a variant",
          })}
          data={variantOptions}
          value={variant}
          onChange={setVariant}
          searchable
          disabled={submitting || variantOptions.length === 0}
          size="xs"
          comboboxProps={{ width: 320, position: "bottom-start" }}
          aria-label={t("jobResultsPage.addRow.variant", {
            defaultValue: "Variant",
          })}
        />
      </Table.Td>

      {/* Value — the reviewer types the raw value here */}
      <Table.Td>
        <Textarea
          autosize
          minRows={1}
          maxRows={4}
          placeholder={t("jobResultsPage.addRow.valuePlaceholder", {
            defaultValue: "Type the raw value",
          })}
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
          disabled={submitting}
          size="xs"
          aria-label={t("jobResultsPage.tmTable.sourceValue", {
            defaultValue: "Value",
          })}
        />
      </Table.Td>

      {/* Item-MP Listing — search + pick the catalogue item */}
      <Table.Td>
        <Select
          placeholder={t("jobResultsPage.addRow.selectPlaceholder", {
            defaultValue: "Search an Item-MP Listing",
          })}
          data={options}
          value={picked ? String(picked.itemMpId) : null}
          onChange={handlePick}
          searchable
          onSearchChange={handleSearchChange}
          // The catalogue is searched server-side; keep every option Mantine
          // was handed instead of filtering the page again on the label.
          filter={({ options }) => options}
          rightSection={itemMp.loading ? <Loader size="xs" /> : undefined}
          nothingFoundMessage={
            itemMp.loading
              ? t("common.loading", { defaultValue: "Searching…" })
              : t("jobResultsPage.addRow.nothingFound", {
                  defaultValue: "Type to search the catalogue",
                })
          }
          disabled={submitting}
          size="xs"
          comboboxProps={{ width: 320, position: "bottom-start" }}
          aria-label={t("jobResultsPage.tmTable.itemMpListing", {
            defaultValue: "Item-MP Listing",
          })}
        />
      </Table.Td>

      {/* ItemMP ID — the picked item's id, mirroring a real row */}
      <Table.Td>
        {picked ? (
          <Text size="sm" ff="monospace">
            {picked.itemMpId}
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            {PLACEHOLDER}
          </Text>
        )}
      </Table.Td>

      {/* value (matched DB value) — resolved after the row is searched */}
      <Table.Td />

      {/* Status */}
      <Table.Td />

      {/* Actions — send (primary) + cancel */}
      <Table.Td>
        <Group gap={4} wrap="nowrap" justify="flex-start">
          <Tooltip
            label={t("jobResultsPage.addRow.submit", {
              defaultValue: "Add row",
            })}
          >
            <ActionIcon
              size="sm"
              variant="filled"
              color="orange"
              disabled={!canSubmit}
              loading={submitting}
              onClick={() => void handleSave()}
              aria-label={t("jobResultsPage.addRow.submit", {
                defaultValue: "Add row",
              })}
            >
              <IconSend size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t("common.cancel", { defaultValue: "Cancel" })}>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="gray"
              disabled={submitting}
              onClick={close}
              aria-label={t("common.cancel", { defaultValue: "Cancel" })}
            >
              <IconX size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>

    </Table.Tr>
  );
};

export default TmAddRow;
